import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mic, MicOff, Square, Loader2, FileAudio, Tag, Clock, Paperclip, Upload, X, FileIcon, Download, ListChecks, CheckCircle2, Zap, BookOpen, ClipboardCopy, Brain, Lightbulb } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RECORDING_SECONDS = 30 * 60; // 30 minutes — Whisper's 25MB cap comfortably fits 30min of WebM/Opus at default bitrate

const VoiceJournalPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [autoCreateTasks, setAutoCreateTasks] = useState(false);
  const [autoCreateNote, setAutoCreateNote] = useState(true);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchJournals();
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchJournals = async () => {
    try {
      const response = await axios.get(`${API}/voice/journals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJournals(response.data);
    } catch (error) {
      toast.error('Failed to fetch voice journals');
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (chunksRef.current.length > 0) {
          await processRecording();
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          if (next === MAX_RECORDING_SECONDS - 300) {
            toast.warning('5 minutes left — recording will auto-stop at 30:00');
          }
          if (next >= MAX_RECORDING_SECONDS) {
            toast.info('Reached 30-minute limit — stopping recording');
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch (error) {
      toast.error('Could not access microphone. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  /**
   * Snap-scroll the transcript box of a given journal to the segment whose
   * start-time is closest to (and not after) the clicked chapter timestamp.
   * Adds a short-lived highlight class so the user sees *where* they jumped to.
   */
  const jumpToChapter = (noteId, startSeconds) => {
    const box = document.getElementById(`transcript-box-${noteId}`);
    if (!box) return;
    const segs = box.querySelectorAll('[data-seg-start]');
    if (!segs.length) return;
    let target = segs[0];
    let bestDelta = Infinity;
    segs.forEach((el) => {
      const s = Number(el.getAttribute('data-seg-start'));
      if (s <= startSeconds) {
        const delta = startSeconds - s;
        if (delta < bestDelta) { bestDelta = delta; target = el; }
      }
    });
    // Scroll the transcript container (not the whole page) to the segment
    box.scrollTo({ top: target.offsetTop - box.offsetTop - 8, behavior: 'smooth' });
    target.classList.add('bg-[#002FA7]/10', 'font-medium');
    setTimeout(() => target.classList.remove('bg-[#002FA7]/10', 'font-medium'), 1600);
  };

  /**
   * Build a clean markdown meeting-note for a single chapter and write it to the
   * clipboard. The chapter's transcript slice is every segment whose start falls
   * in [chapter.start, nextChapter.start). Paste-ready for Slack / email / docs.
   */
  const formatTs = (sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const copyChapterAsMeetingNote = async (journal, chapterIdx) => {
    const chapter = journal.chapters[chapterIdx];
    const next = journal.chapters[chapterIdx + 1];
    const startS = chapter.start_seconds || 0;
    const endS = next ? (next.start_seconds || Infinity) : Infinity;
    const slice = (journal.segments || []).filter(s => (s.start || 0) >= startS && (s.start || 0) < endS);
    const body = slice.length
      ? slice.map(s => `> ${s.text}`).join('\n')
      : `> ${journal.transcript}`;
    const when = journal.created_at ? format(new Date(journal.created_at), 'MMM d, yyyy h:mm a') : '';
    const range = next ? `${formatTs(startS)} → ${formatTs(endS)}` : `${formatTs(startS)} → end`;
    const md = `## ${chapter.title}\n*Voice Journal · ${when} · ${range}*\n\n${body}\n\n— via KStreak Voice Journal`;
    try {
      await navigator.clipboard.writeText(md);
      toast.success('Meeting note copied to clipboard');
    } catch (e) {
      toast.error('Could not copy to clipboard');
    }
  };

  /**
   * Build a focused Decision Advisor prompt from a voice journal and navigate
   * to /pkm?tab=advisor&q=<prompt>. PKMPage auto-fires the query on mount.
   * Prompt grounds the advisor in this specific journal's topics so it can
   * cross-reference against the user's broader knowledge base.
   */
  const askAdvisor = (journal) => {
    const when = journal.created_at ? format(new Date(journal.created_at), 'MMM d, yyyy h:mm a') : '';
    const topicList = journal.chapters?.length
      ? journal.chapters.map(c => c.title).join(', ')
      : (journal.tags?.slice(0, 5).join(', ') || 'the topics covered');
    const summaryLine = journal.summary ? ` Summary of what I said: "${journal.summary}"` : '';
    const prompt =
      `Based on my voice journal from ${when} covering ${topicList}, what is the best next step ` +
      `and what should I watch out for? Cross-reference against everything else in my knowledge base — ` +
      `highlight any contradictions with past decisions, knowledge gaps, or risks you notice.${summaryLine}`;
    navigate(`/pkm?tab=advisor&q=${encodeURIComponent(prompt)}`);
  };

  const processRecording = async () => {
    setIsProcessing(true);
    try {
      const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/voice/journal?auto_create_tasks=${autoCreateTasks}&auto_create_note=${autoCreateNote}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      const { extracted_actions = [], created_task_ids = [] } = response.data || {};
      if (created_task_ids.length > 0) {
        toast.success(`Voice journal processed · ${created_task_ids.length} task${created_task_ids.length === 1 ? '' : 's'} auto-created`);
      } else if (extracted_actions.length > 0) {
        toast.success(`Voice journal processed · ${extracted_actions.length} action${extracted_actions.length === 1 ? '' : 's'} detected`);
      } else {
        toast.success('Voice journal processed successfully!');
      }
      fetchJournals();
    } catch (error) {
      console.error('Processing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process recording');
    } finally {
      setIsProcessing(false);
      setRecordingTime(0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAttachFile = (noteId) => {
    setUploadingFor(noteId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/voice/journal/${uploadingFor}/attachments`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document attached successfully');
      fetchJournals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach document');
    } finally {
      setUploadingFor(null);
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = async (noteId, fileId) => {
    try {
      await axios.delete(`${API}/voice/journal/${noteId}/attachments/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Attachment removed');
      fetchJournals();
    } catch (error) {
      toast.error('Failed to remove attachment');
    }
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  return (
    <div data-testid="voice-journal-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Voice Journal</h1>
          <p className="text-[#4B5563] text-sm">Capture thoughts with AI-powered transcription and summarization</p>
        </div>
      </div>

      {/* Recording Interface */}
      <Card className="border-[#E5E7EB] mb-8">
        <CardContent className="p-8">
          <div className="flex flex-col items-center">
            {/* Waveform Visualization */}
            {isRecording && (
              <div className="flex items-end gap-1 h-16 mb-6">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 bg-[#002FA7] rounded-full waveform-bar"
                    style={{ 
                      animationDelay: `${i * 0.1}s`,
                      height: `${8 + Math.random() * 32}px`
                    }}
                  />
                ))}
              </div>
            )}

            {/* Record Button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-[#FF3B30] recording-pulse' 
                  : 'bg-[#002FA7] hover:bg-[#00227A]'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="record-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              ) : isRecording ? (
                <Square className="w-10 h-10 text-white" />
              ) : (
                <Mic className="w-10 h-10 text-white" />
              )}
            </button>

            {/* Status Text */}
            <div className="mt-4 text-center">
              {isProcessing ? (
                <p className="text-[#4B5563]">Processing your recording...</p>
              ) : isRecording ? (
                <div className="flex items-center gap-2 text-[#FF3B30]" data-testid="recording-timer">
                  <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
                  Recording: {formatTime(recordingTime)} <span className="text-[#9CA3AF] text-xs">/ 30:00</span>
                </div>
              ) : (
                <p className="text-[#4B5563]">Click to start recording (max 30 minutes)</p>
              )}
            </div>

            {/* AI Action Extraction Toggle */}
            <div className="mt-4 pt-4 border-t border-[#E5E7EB] space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="auto-create-tasks-label">
                <input
                  type="checkbox"
                  checked={autoCreateTasks}
                  onChange={(e) => setAutoCreateTasks(e.target.checked)}
                  disabled={isRecording || isProcessing}
                  className="mt-0.5"
                  data-testid="auto-create-tasks-toggle"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-[#002FA7]" />
                    <span className="text-sm font-medium text-[#0A0A0A]">Auto-create tasks from action items</span>
                  </div>
                  <p className="text-xs text-[#4B5563] mt-0.5">
                    AI will detect phrases like "I need to..." or "ask Sarah to..." and create real tasks in <code className="text-[11px] bg-[#F3F4F6] px-1 rounded">/tasks</code>, assigned by name-match.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="auto-create-note-label">
                <input
                  type="checkbox"
                  checked={autoCreateNote}
                  onChange={(e) => setAutoCreateNote(e.target.checked)}
                  disabled={isRecording || isProcessing}
                  className="mt-0.5"
                  data-testid="auto-create-note-toggle"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Brain className="w-3.5 h-3.5 text-[#002FA7]" />
                    <span className="text-sm font-medium text-[#0A0A0A]">Save to Knowledge Engine</span>
                  </div>
                  <p className="text-xs text-[#4B5563] mt-0.5">
                    Also save this journal as a searchable, versioned note in <code className="text-[11px] bg-[#F3F4F6] px-1 rounded">/pkm</code> — with chapters, tags, project, and auto-detected relationships to existing notes.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelected}
        accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
      />

      {/* Journal Entries */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Recent Journals</h2>
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="spinner"></div></div>
        ) : journals.length === 0 ? (
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-12 text-center text-[#9CA3AF]">
              <FileAudio className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
              <p>No voice journals yet. Start recording to create your first entry.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {journals.map((journal) => (
              <Card key={journal.note_id} className="border-[#E5E7EB]" data-testid={`journal-${journal.note_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                      <Clock className="w-4 h-4" />
                      {format(new Date(journal.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                    <div className="flex items-center gap-2">
                      {journal.pkm_note_id && (
                        <a
                          href={`/pkm?note_id=${journal.pkm_note_id}`}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/15 transition-colors"
                          title="View in Knowledge Engine"
                          data-testid={`view-pkm-${journal.note_id}`}
                        >
                          <Brain className="w-3 h-3" />
                          Saved to Knowledge Engine
                        </a>
                      )}
                      {journal.mood && (
                        <Badge variant="secondary">{journal.mood}</Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => askAdvisor(journal)}
                        data-testid={`ask-advisor-btn-${journal.note_id}`}
                        title="Ask Decision Advisor about this journal"
                      >
                        <Lightbulb className="w-3.5 h-3.5 mr-1 text-[#FF9500]" />
                        Ask Advisor
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAttachFile(journal.note_id)}
                        data-testid={`attach-btn-${journal.note_id}`}
                      >
                        <Paperclip className="w-3.5 h-3.5 mr-1" />
                        Attach
                      </Button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">AI Summary</h4>
                    <p className="text-[#0A0A0A]">{journal.summary}</p>
                  </div>

                  {/* Chapters (long recordings only) */}
                  {journal.chapters?.length > 0 && (
                    <div className="mb-4" data-testid={`chapters-${journal.note_id}`}>
                      <h4 className="text-sm font-medium text-[#9CA3AF] mb-2 flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" />
                        Chapters
                        {journal.duration > 0 && (
                          <span className="text-[10px] font-normal">
                            · {Math.floor(journal.duration / 60)}:{String(Math.floor(journal.duration % 60)).padStart(2, '0')} total
                          </span>
                        )}
                      </h4>
                      <div className="space-y-1">
                        {journal.chapters.map((ch, i) => {
                          const s = Math.floor(ch.start_seconds || 0);
                          const mm = Math.floor(s / 60);
                          const ss = String(s % 60).padStart(2, '0');
                          return (
                            <div
                              key={i}
                              className="group flex items-stretch gap-1 rounded bg-[#F3F4F6] hover:bg-[#002FA7]/5 border border-transparent hover:border-[#002FA7]/30 transition-colors"
                            >
                              <button
                                onClick={() => jumpToChapter(journal.note_id, ch.start_seconds)}
                                className="flex-1 text-left flex items-start gap-2 text-xs p-2"
                                data-testid={`chapter-${journal.note_id}-${i}`}
                              >
                                <span className="font-mono text-[#002FA7] font-semibold whitespace-nowrap">{mm}:{ss}</span>
                                <span className="text-[#0A0A0A]">{ch.title}</span>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); copyChapterAsMeetingNote(journal, i); }}
                                className="px-2 text-[#9CA3AF] hover:text-[#002FA7] opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Copy chapter as meeting note (markdown)"
                                aria-label="Copy chapter as meeting note"
                                data-testid={`copy-chapter-${journal.note_id}-${i}`}
                              >
                                <ClipboardCopy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Transcript */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">Full Transcript</h4>
                    {journal.segments?.length > 0 ? (
                      <div
                        className="text-[#4B5563] text-sm bg-[#F3F4F6] p-3 rounded max-h-56 overflow-y-auto space-y-1"
                        data-testid={`transcript-segments-${journal.note_id}`}
                        id={`transcript-box-${journal.note_id}`}
                      >
                        {journal.segments.map((seg, i) => {
                          const s = Math.floor(seg.start || 0);
                          const mm = Math.floor(s / 60);
                          const ss = String(s % 60).padStart(2, '0');
                          return (
                            <div
                              key={i}
                              id={`seg-${journal.note_id}-${Math.floor(seg.start || 0)}`}
                              data-seg-start={Math.floor(seg.start || 0)}
                              className="transition-colors rounded px-1 py-0.5"
                            >
                              <span className="font-mono text-[10px] text-[#9CA3AF] mr-2">{mm}:{ss}</span>
                              {seg.text}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[#4B5563] text-sm bg-[#F3F4F6] p-3 rounded max-h-24 overflow-y-auto">
                        {journal.transcript}
                      </p>
                    )}
                  </div>

                  {/* Attachments */}
                  {journal.attachments?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[#9CA3AF] mb-2">Attachments</h4>
                      <div className="flex flex-wrap gap-2">
                        {journal.attachments.map((att) => (
                          <div
                            key={att.file_id}
                            className="flex items-center gap-2 bg-[#F3F4F6] rounded px-3 py-1.5 text-sm group"
                            data-testid={`attachment-${att.file_id}`}
                          >
                            <FileIcon className="w-3.5 h-3.5 text-[#002FA7]" />
                            <span className="text-[#4B5563] max-w-[150px] truncate">{att.original_filename}</span>
                            <button
                              onClick={() => handleDownloadAttachment(att.file_id, att.original_filename)}
                              className="text-[#002FA7] hover:text-[#00227A]"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleRemoveAttachment(journal.note_id, att.file_id)}
                              className="text-[#9CA3AF] hover:text-[#FF3B30]"
                              title="Remove"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {journal.tags?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-[#9CA3AF]" />
                      {journal.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}

                  {journal.extracted_actions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E7EB]" data-testid={`extracted-actions-${journal.note_id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <ListChecks className="w-4 h-4 text-[#002FA7]" />
                        <span className="text-xs font-semibold text-[#0A0A0A]">
                          Action items detected ({journal.extracted_actions.length})
                        </span>
                        {journal.created_task_ids?.length > 0 && (
                          <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                            {journal.created_task_ids.length} task{journal.created_task_ids.length === 1 ? '' : 's'} created
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        {journal.extracted_actions.map((action, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-2 text-xs p-2 rounded bg-[#FAFAFA] border border-[#F3F4F6]"
                            data-testid={`action-${journal.note_id}-${i}`}
                          >
                            <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold text-white ${
                              action.priority === 'high' ? 'bg-[#FF3B30]' :
                              action.priority === 'low' ? 'bg-[#9CA3AF]' :
                              'bg-[#FF9500]'
                            }`}>{action.priority.toUpperCase()}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[#0A0A0A] font-medium">{action.title}</p>
                              <p className="text-[10px] text-[#4B5563] mt-0.5 flex flex-wrap gap-x-3">
                                {action.assigned_to_name && (
                                  <span>→ <strong>{action.assigned_to_name}</strong></span>
                                )}
                                {action.due_hint && (
                                  <span>⏱ {action.due_hint}</span>
                                )}
                                <span>{Math.round((action.confidence || 0) * 100)}% confidence</span>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceJournalPage;
